import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** Changing this resets the boundary (e.g. the render version). */
  resetKey: string | number | null;
  onReset: () => void;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes in the document subtree so a malformed document
 * can never blank the whole app — it shows a real message and an escape hatch.
 * Resets automatically when a different document is opened (`resetKey`).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced to the console for diagnostics; the UI shows a friendly message.
    console.error('MarkRoom render error:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="m-8 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <p className="font-semibold">This document couldn’t be displayed</p>
          <p className="mt-1 text-sm">
            Something went wrong while rendering it. Try opening a different file.
          </p>
          <button
            type="button"
            onClick={this.props.onReset}
            className="mr-btn mr-btn--primary mt-3"
          >
            Open another file…
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
